import React, { useEffect, useRef, useState } from 'react';
import { AppHeader } from '../components/app-header';
import { AppSideNav } from '../components/app-side-nav';
import { AppFooter } from '../components/app-footer';
import { EventNotificationService, useEventNotification } from '../framework/services/event-notification';
import { AppPages } from './app-pages';
import { BrowserRouter as Router } from 'react-router-dom';
import { getSinglePageAppStyles } from './app-styles';
import { mergeStyles, MessageBarType } from '@fluentui/react';
import { AppMenuEvents } from '../events/app-menu';
import { BwjSemiFlexContainer } from '../framework/components/bwj-semi-flex-container';
import { BwjScrollContainer } from '../framework/components/bwj-scroll-container';
import { PresenterAccountService } from '../services/account';
import { BwjViewManager, BwjViewState } from '../framework/components/bwj-view-manager';
import { xlate, xlationContext } from '../framework/utils/i18n';
import { BwjButtonType } from '../framework/components/bwj-button';
import { ErrorRobotGraphic } from '../components/graphics/error-robot';
import { PresenterAccountPreferencesPanel } from './presenter-account/components/preferences-panel';
import * as preferencesFormService from './presenter-account/form-services/account-preferences';
import { useToast } from '../framework/components/bwj-toast/context';
import { PresenterAccountApiClient } from '../services/presenter-account';
import { PresenterAccountDetailsPanel } from './presenter-account/components/account-panel';

xlationContext('Application root');

const styles = getSinglePageAppStyles();

const subscriberId = 'Component.AppContent';
const resetMainContentScrollEvent = `${subscriberId}.ResetMainContentScroll`

export const AppContent: React.FunctionComponent = () => {
  const [menuAvailable, setMenuAvailable] = useState(false);
  const [showDesktopMenu, setShowDesktopMenu] = useState(true);
  const [invokeShowMobileMenu, setInvokeShowMobileMenu] = useState<{} | undefined>(undefined);
  const [invokeShowAccountPanel, setInvokeShowAccountPanel] = useState<{} | undefined>(undefined);
  const [viewState, setViewState] = useState(BwjViewState.Loading);
  const appInitialized = useRef(false);
  const loadingMessage = useRef(xlate('Loading application...'));
  const [scrollResetInvocation, setScrollResetInvocation] = useState(0);
  const [invokeShowSettings, setInvokeShowSettings] = useState<{} | undefined>(undefined);
  const { showToast } = useToast();
  
  useEventNotification(() => setMenuAvailable(true),[], AppMenuEvents.MenuAvailable, subscriberId);
  useEventNotification(() => setMenuAvailable(false),[], AppMenuEvents.MenuUnavailable, subscriberId);
  useEventNotification(() => setScrollResetInvocation((new Date()).getTime()), [], resetMainContentScrollEvent, subscriberId);
  
  useEffect(() => {
    // only ever do this once.  We do this to ensure that no app view can fire a Menu Available / Unavailable event
    // before this parent component has subscribed to it
    if(appInitialized.current === false) {
      appInitialized.current = true;
      PresenterAccountService.initialize()
      .then(() => {
        preferencesFormService.initializePreferences();
        window.setTimeout(() => setViewState(BwjViewState.Ready), 100);
      })
      .catch(() => setViewState(BwjViewState.Error));
    }
  });

  const appBodyClass = mergeStyles([styles.root]);
  // const mainSectionClass = mergeStyles([styles.main]);
  const contentAreaClass = mergeStyles([styles.contentArea]);

  const onSignOut = (signOutAllDevices: boolean) => {
    setMenuAvailable(false);
    loadingMessage.current = xlate('Signing out....');
    setViewState(BwjViewState.Loading);
    
    if(signOutAllDevices) {
      PresenterAccountService.logOutAllDevices();
    } else {
      PresenterAccountService.logOutThisDevice();
    }
  }

  const onRestartApp = () => {
    window.location.reload();
  }

  const onSavePreferences = () => {
    const request = preferencesFormService.toRequest();
    PresenterAccountApiClient.updateAccountPreferences(request)
    .then(() => {
      preferencesFormService.setSavePoint();
      showToast(MessageBarType.success, xlate('Preferences successfully updated'));
    })
    .catch(() => {
      preferencesFormService.revertChanges();
      showToast(MessageBarType.error, xlate('An error occurred - preferences not saved'));
    });
  };

  return (
    <Router>
      <PresenterAccountPreferencesPanel
        invokeShowPanel={invokeShowSettings}
        onSavePreferences={onSavePreferences}
      />
      <PresenterAccountDetailsPanel
        invokeShowPanel={invokeShowAccountPanel}
        onSignOutThisDevice={() => onSignOut(false)}
        onSignOutAllDevices={() => onSignOut(true)}
      />
      <div className={appBodyClass}>
        <AppHeader
          showMenuButton={menuAvailable}
          onShowSettings={() => setInvokeShowSettings({})}
          onDesktopMenuButtonClick={() => setShowDesktopMenu(!showDesktopMenu)}
          onMobileMenuButtonClick={() => setInvokeShowMobileMenu({})}
          onShowAccount={() => setInvokeShowAccountPanel({})}
        />
        <BwjSemiFlexContainer
          styles={{ root: { flexGrow: 1 }, content: { display: 'flex', flexDirection: 'row', alignItems: 'stretch', position: 'relative', } }}
          pinWidth={false}
          pinHeight={true}
        >
          {menuAvailable &&
          <AppSideNav
            invokeShowMobileNav={invokeShowMobileMenu}
            showDesktopNav={showDesktopMenu}
          />}
          <section className={contentAreaClass}>
            <BwjViewManager
              state={viewState}
              loadingState={{
                message: loadingMessage.current,
              }}
              errorState={{
                graphic: ErrorRobotGraphic,
                graphicPortraitOriented: true,
                message: xlate('We could not start the app due to an error - try restarting the app.  Please be sure to check your Internet connection'),
                buttons: [{
                  buttonStyle: BwjButtonType.PrimaryStandard,
                  text: xlate('Restart app'),
                  onClick: onRestartApp,
                }],
              }}
            >
              <BwjScrollContainer
                containerStyle={{ display: 'flex', flexDirection: 'column', overflowX: 'hidden', }}
                invokeScrollReset={scrollResetInvocation}
              >
                <AppPages />
              </BwjScrollContainer>
            </BwjViewManager>
          </section>
        </BwjSemiFlexContainer>
        <AppFooter />
      </div>
    </Router>
  );
};

export const useMainContentScrollReset = () => {
  const fn = () => EventNotificationService.dispatch(resetMainContentScrollEvent);
  return fn;
}
